import * as THREE from 'three';

import { axialToWorld, roundAxial, worldToAxial } from '../map/HexCoordinates';
import { HEX_SIZE } from '../map/HexMetrics';
import type { Anchor, Anchors } from './Anchors';
import type { LoadedModel } from './BuildingModels';

/**
 * What a building looks like: a model where one has arrived, a silhouette
 * where none has.
 *
 * **Sylwetki są zastępcze i mają tak wyglądać.** Rodzaj bez pliku jest bryłą
 * budowaną w kodzie: namiot na obóz, chata ze spadzistym dachem, chata z pomostem
 * na przystań, wycięty stopień na kamieniołom, zaorany arkusz na pole, beczka na
 * wszystko, czego ten build nie zna.
 *
 * **Model wygrywa z sylwetką i zabiera ze sobą kolor właściciela.** Budynek bez
 * modelu jest malowany barwą wyliczoną z nazwy właściciela i tak na mapie widać,
 * czyj jest; model przynosi własne tekstury i pomnożenie ich przez tę barwę
 * zamieniłoby go w plamę. Czyj jest, mówi wtedy dymek pod kursorem. Jest to więc
 * **przejściowa niespójność** i taka ma być: rodzaje dostają pliki po jednym,
 * a kolor właściciela znika wtedy, kiedy przestaje być jedyną rzeczą, po której
 * ten budynek da się rozpoznać.
 *
 * Jedno miejsce, bo rysują je trzy rzeczy: to, co stoi na mapie, duch pod
 * kursorem i miniatura w panelu budowania. Gdyby każda miała własne kształty,
 * rozjechałyby się, a podgląd przestałby być podglądem.
 */

/** How much of a hex a building takes up. Smaller than a prop: it is a marker. */
const FOOTPRINT = HEX_SIZE * 0.62;

function tent(): THREE.BufferGeometry {
  const height = FOOTPRINT * 1.5;
  const geometry = new THREE.ConeGeometry(FOOTPRINT, height, 6);
  geometry.translate(0, height / 2, 0);
  return geometry;
}

function hut(): THREE.BufferGeometry {
  const wallHeight = FOOTPRINT * 0.9;
  const roofHeight = FOOTPRINT * 0.8;

  const walls = new THREE.BoxGeometry(FOOTPRINT * 1.4, wallHeight, FOOTPRINT * 1.2);
  walls.translate(0, wallHeight / 2, 0);

  // Four-sided cone, turned an eighth so its ridge runs along the walls rather
  // than across their corners.
  const roof = new THREE.ConeGeometry(FOOTPRINT * 1.1, roofHeight, 4);
  roof.rotateY(Math.PI / 4);
  roof.translate(0, wallHeight + roofHeight / 2, 0);

  return merge(walls, roof);
}

/**
 * A hut on posts with a jetty running out of it — the fishery.
 *
 * The jetty is the whole point of the silhouette: it is the one building that
 * has to read as standing **at** the water rather than on the ground, and a
 * shape that did not lean towards something would look like the lumberjack's
 * hut in a different place. Which way it leans is not known here — the shape is
 * built once and shared by every one of them — so it points along +X and the
 * ground it points at is whatever is there.
 */
function pier(): THREE.BufferGeometry {
  const wallHeight = FOOTPRINT * 0.8;
  const roofHeight = FOOTPRINT * 0.6;

  const walls = new THREE.BoxGeometry(FOOTPRINT * 1.1, wallHeight, FOOTPRINT * 1.1);
  walls.translate(-FOOTPRINT * 0.3, wallHeight / 2, 0);

  const roof = new THREE.ConeGeometry(FOOTPRINT * 0.95, roofHeight, 4);
  roof.rotateY(Math.PI / 4);
  roof.translate(-FOOTPRINT * 0.3, wallHeight + roofHeight / 2, 0);

  const deck = new THREE.BoxGeometry(FOOTPRINT * 1.5, FOOTPRINT * 0.12, FOOTPRINT * 0.5);
  deck.translate(FOOTPRINT * 0.75, FOOTPRINT * 0.24, 0);

  return merge(merge(walls, roof), deck);
}

/**
 * A step cut into the rock — the quarry.
 *
 * Two blocks, the lower one wider, so it reads as ground taken away rather than
 * a building put down. It stands on a boulder field, where every neighbour is
 * already lumpy, and a pitched roof there would be lost among the props.
 */
function bench(): THREE.BufferGeometry {
  const lowHeight = FOOTPRINT * 0.45;
  const highHeight = FOOTPRINT * 0.5;

  const low = new THREE.BoxGeometry(FOOTPRINT * 1.6, lowHeight, FOOTPRINT * 1.3);
  low.translate(0, lowHeight / 2, 0);

  const high = new THREE.BoxGeometry(FOOTPRINT * 0.9, highHeight, FOOTPRINT * 1.3);
  high.translate(-FOOTPRINT * 0.35, lowHeight + highHeight / 2, 0);

  return merge(low, high);
}

/**
 * Zaorana płachta — pole uprawne.
 *
 * Płaska i szeroka, bo to jedyny „budynek", który niczego nie buduje: zajmuje
 * pole i ma być widoczny z góry jako **kawałek innej ziemi**, a nie jako
 * sylwetka. Dwie skiby na wierzchu wystarczą, żeby z izometrii odróżnić je od
 * fundamentu czegoś, co dopiero rośnie.
 */
function furrows(): THREE.BufferGeometry {
  const bedHeight = FOOTPRINT * 0.12;
  const ridgeHeight = FOOTPRINT * 0.1;

  const bed = new THREE.BoxGeometry(FOOTPRINT * 1.7, bedHeight, FOOTPRINT * 1.5);
  bed.translate(0, bedHeight / 2, 0);
  let shape: THREE.BufferGeometry = bed;

  for (const offset of [-FOOTPRINT * 0.4, 0, FOOTPRINT * 0.4]) {
    const ridge = new THREE.BoxGeometry(FOOTPRINT * 1.6, ridgeHeight, FOOTPRINT * 0.22);
    ridge.translate(0, bedHeight + ridgeHeight / 2, offset);
    shape = merge(shape, ridge);
  }

  return shape;
}

/**
 * Wieża na podmurówce — warownia.
 *
 * **Wysoka i wąska, bo ma być widoczna zza wzgórza.** Warownia stoi tam, gdzie
 * trzeba, a nie tam, gdzie płasko — często więc na stoku, gdzie niska bryła
 * ginie za grzbietem. Blanek nie ma i nie będzie: sylwetka zastępcza ma mówić
 * „coś tu stoi i jest to obronne", a nie udawać modelu, który przyjdzie.
 */
function tower(): THREE.BufferGeometry {
  const baseHeight = FOOTPRINT * 0.4;
  const shaftHeight = FOOTPRINT * 1.6;

  const base = new THREE.CylinderGeometry(FOOTPRINT * 1.05, FOOTPRINT * 1.15, baseHeight, 6);
  base.translate(0, baseHeight / 2, 0);

  const shaft = new THREE.CylinderGeometry(FOOTPRINT * 0.72, FOOTPRINT * 0.8, shaftHeight, 6);
  shaft.translate(0, baseHeight + shaftHeight / 2, 0);

  // Okap: szersze zwieńczenie czyta się z izometrii jako korona murów, a nie
  // jako koniec walca uciętego w powietrzu.
  const crownHeight = FOOTPRINT * 0.22;
  const crown = new THREE.CylinderGeometry(FOOTPRINT * 0.92, FOOTPRINT * 0.92, crownHeight, 6);
  crown.translate(0, baseHeight + shaftHeight + crownHeight / 2, 0);

  // `merge` bierze dwie bryły, więc trzy składa się dwoma krokami — tak samo
  // jak robi to `bench` dla swoich dwóch.
  return merge(merge(base, shaft), crown);
}

function drum(): THREE.BufferGeometry {
  const height = FOOTPRINT * 1.1;
  const geometry = new THREE.CylinderGeometry(FOOTPRINT, FOOTPRINT, height, 6);
  geometry.translate(0, height / 2, 0);
  return geometry;
}

/**
 * Two geometries in one buffer.
 *
 * Written out rather than pulled from `three-stdlib`: it is a concatenation of
 * two position arrays, and a dependency for that would be a dependency for
 * nothing.
 */
function merge(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
  const first = a.toNonIndexed();
  const second = b.toNonIndexed();

  const positions = new Float32Array(
    first.attributes.position.count * 3 + second.attributes.position.count * 3
  );
  positions.set(first.attributes.position.array as Float32Array, 0);
  positions.set(
    second.attributes.position.array as Float32Array,
    first.attributes.position.count * 3
  );

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.computeVertexNormals();

  first.dispose();
  second.dispose();
  a.dispose();
  b.dispose();
  return merged;
}

/**
 * Jak wysoki jest budynek z pliku i jak szeroki mu wolno być.
 *
 * Model przyjeżdża znormalizowany do jedynki wysokości (`flatten`), więc skala
 * jest tu jedną liczbą — a szerokość jest **ograniczeniem, nie wymiarem**:
 * plik, który wróci z eksportu przysadzisty, ma zrobić się niższy, a nie rozlać
 * się na sąsiednie pola. Ten sam rachunek, którym stawiane są drzewa.
 *
 * **Miarą jest heks, nie sylwetka zastępcza.** Heks o promieniu `HEX_SIZE` ma
 * w poprzek płaskich boków `√3 × HEX_SIZE`, czyli 0,866 — a 1,7 × `HEX_SIZE`
 * to 0,85, więc budynek wypełnia pole prawie w całości i zostaje tyle, ile
 * trzeba, żeby dwa sąsiednie się nie zetknęły. Pierwsza wersja mierzyła się
 * `FOOTPRINT`-em zastępczych brył (0,45 szerokości) i to był błąd odziedziczony
 * po nich: sylwetka jest **znacznikiem** i ma prawo być mała, a model jest tym
 * budynkiem i ma stać na swoim polu.
 *
 * Wysokość jest sufitem, nie wymiarem, i dlatego jest odrobinę większa od
 * szerokości: model smukły ma urosnąć do niego, a nie w wieżę.
 */
const MODEL_HEIGHT = HEX_SIZE * 1.8;
const MODEL_MAX_WIDTH = HEX_SIZE * 1.7;

/**
 * Klucz rusztowania — tego, co stoi **na** budynku w czasie budowy.
 *
 * Nie jest rodzajem budynku i nigdy nie będzie: nie ma wpisu w katalogu, nie da
 * się go postawić i nie da się go kliknąć jako rzecz osobną. Jest tu, bo skala
 * modelu wypieka się w geometrię w jednym miejscu, a rusztowanie jest modelem
 * jak każdy inny. Pyta się o nie `scaffoldLook`, a nie `lookFor` — inaczej
 * literówka w kluczu rodzaju wyciągnęłaby rusztowanie zamiast beczki i wyglądała
 * na działającą.
 */
export const SCAFFOLDING = 'scaffolding';

/** Jedna bryła do narysowania: geometria i — u modeli — jej własny materiał. */
export interface BuildingPart {
  geometry: THREE.BufferGeometry;
  /** Brak znaczy „pomaluj kolorem właściciela". */
  material?: THREE.Material;
}

export interface BuildingLook {
  parts: BuildingPart[];
  /** Wysokość w jednostkach świata — pod paski postępu i dymki. */
  height: number;
  /** Czy to plik. Kolor właściciela dotyczy wyłącznie sylwetek. */
  modelled: boolean;
  /**
   * Skąd ta bryła świeci i skąd dymi — **w jednostkach świata, względem
   * podstawy**, jeszcze nieobrócone.
   *
   * Przeliczone tą samą skalą, którą wypieka się w geometrię, i z tego samego
   * powodu: wszystko poniżej dostaje wtedy punkt gotowy do dodania do pozycji
   * budynku i żadne z nich nie musi wiedzieć, że plik przyjechał w innej skali.
   * Obrót dokłada `BuildingField`, bo należy do postawionego budynku, a nie do
   * bryły — ta jest współdzielona przez wszystkie tego rodzaju w świecie.
   *
   * **Sylwetki z kodu mają te listy puste** i to jest ich odpowiedź: nie ma
   * pliku, nie ma na czym stawiać markera, a ogień i dym wracają tam, gdzie
   * siadały, zanim punkty istniały.
   */
  hearth: THREE.Vector3[];
  smoke: THREE.Vector3[];
  /** Gdzie stoi drzewce sztandaru właściciela — pierwszy się liczy. */
  banner: THREE.Vector3[];
  /**
   * Środki pól, na które bryła wychodzi poza własne — względem środka
   * własnego, jeszcze nieobrócone. Teren pod nimi obniża się do wysokości
   * budynku (`BuildingField.lowered`).
   */
  overhang: THREE.Vector3[];
}

/**
 * O ile wierzchołek może wystawać za krawędź pola, zanim sąsiad się liczy —
 * ułamek odległości od środka. Okap sięgający odrobinę za brzeg nie ma kopać
 * dołu u sąsiada; pomost sięgający w głąb jego pola — ma.
 */
const OVERHANG_SLACK = 0.15;

/** Pola, na które wychodzą wierzchołki tej bryły — patrz `BuildingLook.overhang`. */
function overhangOf(parts: BuildingPart[]): THREE.Vector3[] {
  const found = new Map<string, THREE.Vector3>();
  for (const { geometry } of parts) {
    const position = geometry.getAttribute('position');
    if (!position) continue;
    for (let i = 0; i < position.count; i++) {
      const shrink = 1 - OVERHANG_SLACK;
      const fractional = worldToAxial(position.getX(i) * shrink, position.getZ(i) * shrink);
      const cell = roundAxial(fractional.q, fractional.r);
      if (cell.q === 0 && cell.r === 0) continue;
      const id = `${cell.q},${cell.r}`;
      if (found.has(id)) continue;
      const { x, z } = axialToWorld(cell.q, cell.r);
      found.set(id, new THREE.Vector3(x, 0, z));
    }
  }
  return [...found.values()];
}

const SHAPES = new Map<string, THREE.BufferGeometry>();
const LOOKS = new Map<string, BuildingLook>();
const MODELS = new Map<string, BuildingLook>();

/**
 * Przyjmij modele wczytane z plików.
 *
 * Skala wypiekana jest w geometrię **raz, tutaj**, a nie nakładana na obiekt
 * przy każdym postawieniu: wszystko poniżej — postawiony budynek, duch pod
 * kursorem, miniatura w panelu — dostaje wtedy bryłę gotową do narysowania
 * i żadne z nich nie musi wiedzieć, że plik przyjechał w innej skali.
 */
export function adoptModels(models: LoadedModel[], anchors: Anchors = {}): void {
  for (const { key, level, model, composed } of models) {
    // **Kompozycja nie jest dopasowywana do pola**: jej przestrzeń to heks
    // o promieniu jeden, więc do świata przechodzi skalą heksa i wystaje poza
    // pole dokładnie tam, gdzie ją tak ustawiono.
    const scale = composed
      ? HEX_SIZE
      : Math.min(MODEL_HEIGHT, MODEL_MAX_WIDTH / Math.max(model.footprint, 1e-6));

    // Punkty idą przez **tę samą skalę**, co geometria, bo są zapisane w tej
    // samej przestrzeni — znormalizowanej, którą zostawia po sobie `flatten`.
    // Osobna liczba tutaj byłaby drugą regułą o tej samej rzeczy i rozjechałaby
    // się przy pierwszym modelu szerszym niż wysoki, gdzie o skali rozstrzyga
    // szerokość, a nie wysokość.
    const placed = composed ?? anchors[model.name] ?? {};
    const scaled = (points?: Anchor[]) =>
      (points ?? []).map(([x, y, z]) => new THREE.Vector3(x * scale, y * scale, z * scale));

    const parts = model.parts.map((part) => {
      const geometry = part.geometry.clone();
      geometry.scale(scale, scale, scale);
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      return { geometry, material: part.material };
    });

    // Wysokość znormalizowana do jedynki razy skala — czyli sama skala. Kompozycja
    // ma wysokość swoich części, więc się ją mierzy.
    let height = scale;
    if (composed) {
      height = 0;
      for (const part of parts) height = Math.max(height, part.geometry.boundingBox?.max.y ?? 0);
    }
    MODELS.set(rung(key, level), {
      parts,
      height,
      modelled: true,
      hearth: scaled(placed.hearth),
      smoke: scaled(placed.smoke),
      banner: scaled(placed.banner),
      overhang: overhangOf(parts),
    });
    LOOKS.clear();
  }
}

/**
 * Rusztowanie, jeśli plik przyjechał.
 *
 * `undefined` znaczy, że budowa wygląda tak, jak wyglądała, zanim ten plik
 * istniał — czyli po prostu bez rusztowania. Brak jednego modelu nie jest
 * powodem, żeby świat się nie otworzył.
 */
export function scaffoldLook(): BuildingLook | undefined {
  return MODELS.get(SCAFFOLDING);
}

/**
 * Klucz szukania: rodzaj na szczeblu, albo rodzaj na każdym.
 *
 * Prywatny, bo kształt tego klucza nie obchodzi nikogo poza tym plikiem —
 * na zewnątrz pyta się o parę (rodzaj, szczebel) i tyle.
 */
function rung(key: string, level?: number): string {
  return level === undefined ? key : `${key}@${level}`;
}

/**
 * Czym ten rodzaj jest do narysowania.
 *
 * Jedno pytanie dla modeli i sylwetek, bo trzy miejsca, które rysują budynki,
 * zadają je tak samo i żadne z nich nie ma powodu wiedzieć, które to które.
 */
export function lookFor(key: string, level = 1): BuildingLook {
  // Szczebel wygrywa z rodzajem: wioska jest obozem na drugim szczeblu i ma
  // swój plik, a drwal ma jeden na wszystkie.
  const model = MODELS.get(rung(key, level)) ?? MODELS.get(key);
  if (model) return model;

  const known = LOOKS.get(key);
  if (known) return known;

  const geometry = shapeFor(key);
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const look: BuildingLook = {
    parts: [{ geometry }],
    height: geometry.boundingBox?.max.y ?? 0,
    modelled: false,
    hearth: [],
    smoke: [],
    banner: [],
    overhang: [],
  };
  LOOKS.set(key, look);
  return look;
}

/**
 * Zastępcza sylwetka rodzaju, budowana raz.
 *
 * Wewnętrzna: na zewnątrz pyta się `lookFor`, bo nikt poza tym plikiem nie ma
 * powodu wiedzieć, czy rodzaj ma już swój plik, czy jeszcze nie.
 */
function shapeFor(key: string): THREE.BufferGeometry {
  const known = SHAPES.get(key);
  if (known) return known;

  const shape =
    key === 'camp'
      ? tent()
      : key === 'lumberjack'
        ? hut()
        : key === 'fishery'
          ? pier()
          : key === 'quarry'
            ? bench()
            : key === 'field'
              ? furrows()
              : key === 'hunter'
                ? hut()
                : key === 'stronghold'
                  ? tower()
                  : drum();
  SHAPES.set(key, shape);
  return shape;
}

/**
 * How tall a kind stands, in world units.
 *
 * Measured off the geometry rather than written down beside it: a height that
 * has to be kept in step by hand is a height that will not be — and it is now
 * measured off **whichever** of the two the kind has, so a file arriving
 * changes where the label over it hangs without anybody moving it.
 */
export function heightOf(key: string, level = 1): number {
  return lookFor(key, level).height;
}

/** Let go of every shape. Only for shutting the game down. */
export function disposeShapes(): void {
  for (const shape of SHAPES.values()) shape.dispose();
  SHAPES.clear();
  for (const look of MODELS.values()) {
    for (const part of look.parts) {
      part.geometry.dispose();
      part.material?.dispose();
    }
  }
  MODELS.clear();
  LOOKS.clear();
}
